import { useRef, useState } from "react";
import { MapPin } from "lucide-react";

const PROJECTS = [
  {
    location: "Burnaby, BC",
    city: "Burnaby",
    type: "Full Asphalt Reroof",
    before:
      "https://images.unsplash.com/photo-1609098702236-e1adbea149dd?crop=entropy&cs=srgb&fm=webp&q=85&w=1400",
    after:
      "https://images.unsplash.com/photo-1601658797866-7ae55d05a817?crop=entropy&cs=srgb&fm=webp&q=85&w=1400",
    altBefore: "Worn asphalt shingle roof before replacement — Crestline Roofing Ltd. Burnaby",
    altAfter: "Completed asphalt shingle roof installation — Crestline Roofing Ltd. Burnaby",
  },
  {
    location: "North Vancouver, BC",
    city: "North Vancouver",
    type: "Metal Roof Installation",
    before:
      "https://images.unsplash.com/photo-1706799314794-2ff701acfb5b?crop=entropy&cs=srgb&fm=webp&q=85&w=1400",
    after:
      "https://images.unsplash.com/photo-1779941373840-a93f6e0d5db0?crop=entropy&cs=srgb&fm=webp&q=85&w=1400",
    altBefore: "Aged metal roof before installation — Crestline Roofing Ltd. North Vancouver",
    altAfter: "New standing-seam metal roof installation — Crestline Roofing Ltd. North Vancouver",
  },
  {
    location: "Surrey, BC",
    city: "Surrey",
    type: "Asphalt Shingle Replacement",
    before:
      "https://images.unsplash.com/photo-1686835162294-c4cd78c7eab6?crop=entropy&cs=srgb&fm=webp&q=85&w=1400",
    after:
      "https://images.unsplash.com/photo-1573876384005-dabc14a8ccbd?crop=entropy&cs=srgb&fm=webp&q=85&w=1400",
    altBefore: "Damaged asphalt shingle roof before replacement — Crestline Roofing Ltd. Surrey",
    altAfter: "Completed asphalt shingle roof replacement — Crestline Roofing Ltd. Surrey",
  },
];

function BeforeAfter({ before, after, idx, altBefore, altAfter }) {
  const wrapRef = useRef(null);
  const [pos, setPos] = useState(55);
  const [dragging, setDragging] = useState(false);

  const setFromClientX = (clientX) => {
    if (!wrapRef.current) return;
    const rect = wrapRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const pct = Math.max(4, Math.min(96, (x / rect.width) * 100));
    setPos(pct);
  };

  return (
    <div
      ref={wrapRef}
      data-testid={`project-slider-${idx}`}
      className="relative w-full aspect-[4/3] overflow-hidden select-none cursor-ew-resize bg-[#0a0a0a]"
      onMouseMove={(e) => dragging && setFromClientX(e.clientX)}
      onMouseDown={(e) => {
        setDragging(true);
        setFromClientX(e.clientX);
      }}
      onMouseUp={() => setDragging(false)}
      onMouseLeave={() => setDragging(false)}
      onTouchStart={(e) => {
        setDragging(true);
        setFromClientX(e.touches[0].clientX);
      }}
      onTouchMove={(e) => dragging && setFromClientX(e.touches[0].clientX)}
      onTouchEnd={() => setDragging(false)}
    >
      {/* After (base) */}
      <img
        src={after}
        alt={altAfter}
        width="1400"
        height="1050"
        loading="lazy"
        decoding="async"
        className="absolute inset-0 w-full h-full object-cover"
        draggable={false}
      />
      {/* Before (clipped) */}
      <div
        className="absolute inset-0 overflow-hidden"
        style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
      >
        <img
          src={before}
          alt={altBefore}
          width="1400"
          height="1050"
          loading="lazy"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover"
          draggable={false}
        />
      </div>

      {/* Labels */}
      <span className="absolute top-4 left-4 z-10 font-display uppercase tracking-[0.3em] text-[10px] font-bold px-3 py-1.5 bg-[#121212]/85 text-white border border-white/15">
        Before
      </span>
      <span className="absolute top-4 right-4 z-10 font-display uppercase tracking-[0.3em] text-[10px] font-bold px-3 py-1.5 bg-[var(--crest-amber)] text-[#121212]">
        After
      </span>

      {/* Divider line + handle */}
      <div
        className="absolute top-0 bottom-0 w-[2px] bg-[var(--crest-amber)] z-10 pointer-events-none"
        style={{ left: `${pos}%`, transform: "translateX(-1px)" }}
      >
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-[var(--crest-amber)] border-2 border-[#121212] flex items-center justify-center shadow-[0_8px_24px_rgba(217,119,6,0.5)]">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path
              d="M8 6L2 12L8 18M16 6L22 12L16 18"
              stroke="#121212"
              strokeWidth="2.5"
              strokeLinecap="square"
            />
          </svg>
        </div>
      </div>
    </div>
  );
}

export default function Projects() {
  return (
    <section
      id="projects"
      data-testid="projects-section"
      className="relative shingle-texture bg-[#0d1320] py-24 md:py-32"
    >
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-3xl mb-16">
          <div className="reveal flex items-center gap-3 mb-6">
            <span className="h-px w-12 bg-[var(--crest-amber)]" />
            <span className="font-display uppercase tracking-[0.4em] text-xs font-semibold text-[var(--crest-amber)]">
              Project Archive
            </span>
          </div>
          <h2
            data-testid="projects-heading"
            className="reveal font-display uppercase font-extrabold text-white leading-[0.95] tracking-tight"
            style={{ fontSize: "clamp(2.4rem, 5.5vw, 4.5rem)" }}
          >
            Serving Your
            <br />
            <span className="text-white/50">Neighbourhood.</span>
          </h2>
          <p className="reveal font-body text-base text-white/60 mt-6 max-w-xl">
            Real homes. Real crews. Real shingles. Every project documented from
            tear-off to final magnetic sweep — drag any slider to see the before
            and after.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-5">
          {PROJECTS.map((p, i) => (
            <div
              key={i}
              data-testid={`project-card-${i}`}
              className="reveal card-lift bg-[#0a0a0a] border border-white/10 overflow-hidden"
              style={{ transitionDelay: `${i * 120}ms` }}
            >
              <BeforeAfter
                before={p.before}
                after={p.after}
                idx={i}
                altBefore={p.altBefore}
                altAfter={p.altAfter}
              />
              <div className="p-6 flex flex-col gap-2">
                <span className="font-display uppercase tracking-[0.3em] text-[10px] font-semibold text-[var(--crest-amber)]">
                  {p.type}
                </span>
                <div className="flex items-center gap-2 text-white/85">
                  <MapPin size={14} className="text-white/40" />
                  <span className="font-display uppercase font-bold text-lg tracking-tight">
                    {p.location}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
